import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { endoReportService } from '../../services/endoReportService';
import { contactService } from '../../services/contactService';
import { referralService } from '../../services/referralService';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowNavigation from '../../components/ui/WorkflowNavigation';
import PatientSelectionStep from './components/PatientSelectionStep';
import ReportTypeStep from './components/ReportTypeStep';
import DictationStep from './components/DictationStep';
import ReviewEditStep from './components/ReviewEditStep';
import RefinementStep from './components/RefinementStep';
import ImageUploadStep from './components/ImageUploadStep';
import { reportImageStorageService } from '../../services/reportImageStorageService';
import toast from 'react-hot-toast';

const AIReportGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [generatedContent, setGeneratedContent] = useState('');
  const [draftReportId, setDraftReportId] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    // Patient selection
    patientSource: 'manual',
    selectedReferralId: '',
    patientFirstName: '',
    patientLastName: '',
    patientYearOfBirth: '',
    patientIdReference: '',
    
    // Referrer information
    referrerName: '',
    referrerSpecialty: '',
    referrerPractice: '',
    referrerEmail: '',
    referrerPhone: '',
    
    // Report type
    reportType: 'consultation',
    
    // Clinical data
    toothNumbers: '',
    diagnosis: '',
    procedures: '',
    
    // Source notes (dictation)
    sourceNotes: ''
  });

  const stepLabels = [
    'Patient',
    'Type de CR',
    'Dictée',
    'Images',
    'Vérification',
    'Ajustements'
  ];

  // Load contacts and referrals
  useEffect(() => {
    if (userProfile?.practitionerId) {
      loadData();
    }
  }, [userProfile?.practitionerId]);

  // Pre-fill form from referral data if provided via navigation state
  useEffect(() => {
    if (location?.state?.referralData) {
      const referralData = location?.state?.referralData;
      
      const clinicalNotesFromReferral = buildClinicalNotesFromReferral(referralData);
      
      setFormData(prev => ({
        ...prev,
        patientSource: 'existing',
        selectedReferralId: referralData?.referralId || '',
        patientFirstName: referralData?.patientFirstName || '',
        patientLastName: referralData?.patientLastName || '',
        patientYearOfBirth: referralData?.patientYearOfBirth?.toString() || '',
        referrerName: referralData?.referringDentistName || '',
        referrerPractice: referralData?.referringPracticeName || '',
        referrerEmail: referralData?.referringDentistEmail || '',
        referrerPhone: referralData?.referringDentistPhone || '',
        toothNumbers: referralData?.toothNumbers?.join(', ') || '',
        diagnosis: referralData?.diagnosis || '',
        sourceNotes: clinicalNotesFromReferral
      }));
      
      toast?.success('Données du patient pré-remplies depuis l\'adressage');
    }
  }, [location?.state?.referralData]);

  const buildClinicalNotesFromReferral = (referralData) => {
    let notes = [];
    
    if (referralData?.patientFirstName && referralData?.patientLastName) {
      notes?.push(`Patient: ${referralData?.patientFirstName} ${referralData?.patientLastName}`);
    }
    
    if (referralData?.patientYearOfBirth) {
      notes?.push(`Année de naissance: ${referralData?.patientYearOfBirth}`);
    }
    
    if (referralData?.toothNumbers && referralData?.toothNumbers?.length > 0) {
      notes?.push(`\nDent(s) concernée(s): ${referralData?.toothNumbers?.join(', ')}`);
    }
    
    if (referralData?.referralReasons && referralData?.referralReasons?.length > 0) {
      notes?.push(`\nMotif d'adressage: ${referralData?.referralReasons?.join(', ')}`);
    }
    
    if (referralData?.urgency) {
      const urgencyLabels = {
        routine: 'Routine',
        urgent: 'Urgent',
        emergency: 'Urgence'
      };
      notes?.push(`Urgence: ${urgencyLabels?.[referralData?.urgency] || referralData?.urgency}`);
    }
    
    if (referralData?.clinicalNotes) {
      notes?.push(`\nNotes cliniques:\n${referralData?.clinicalNotes}`);
    }
    
    if (referralData?.referringDentistName) {
      notes?.push(`\nAdressé par: Dr ${referralData?.referringDentistName}`);
      if (referralData?.referringPracticeName) {
        notes?.push(`Cabinet: ${referralData?.referringPracticeName}`);
      }
    }
    
    return notes?.join('\n');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsData, referralsData] = await Promise.all([
        contactService?.searchContacts(userProfile?.practitionerId, ''),
        referralService?.getAll(userProfile?.practitionerId)
      ]);
      setContacts(contactsData || []);
      setReferrals(referralsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast?.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleReferralSelection = (referralId) => {
    const referral = referrals?.find(r => r?.id === referralId);
    if (referral) {
      setFormData(prev => ({
        ...prev,
        selectedReferralId: referralId,
        patientFirstName: referral?.patientFirstName || '',
        patientLastName: referral?.patientLastName || '',
        patientYearOfBirth: referral?.patientYearOfBirth || '',
        referrerName: referral?.referringDentistName || '',
        referrerPractice: referral?.referringPracticeName || '',
        referrerEmail: referral?.referringDentistEmail || '',
        referrerPhone: referral?.referringDentistPhone || '',
        toothNumbers: referral?.toothNumbers?.join(', ') || ''
      }));
    }
  };

  const handleReferrerSelection = (contactId) => {
    const contact = contacts?.find(c => c?.id === contactId);
    if (contact) {
      setFormData(prev => ({
        ...prev,
        referrerName: contact?.dentistName || '',
        referrerPractice: contact?.practiceName || '',
        referrerEmail: contact?.email || '',
        referrerPhone: contact?.phone || ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData?.patientFirstName || !formData?.patientLastName) {
          toast?.error('Veuillez renseigner le nom et prénom du patient');
          return false;
        }
        return true;
      case 2:
        if (!formData?.reportType) {
          toast?.error('Veuillez sélectionner un type de compte rendu');
          return false;
        }
        return true;
      case 3:
        if (!formData?.sourceNotes?.trim()) {
          toast?.error('Veuillez saisir des notes ou une dictée');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    // If moving from step 3 to step 4, skip to step 5 (images optional)
    // If moving from step 4 (images) to step 5, generate AI report
    if (currentStep === 4) {
      await generateReport();
    }

    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleCancel = () => {
    if (window.confirm('Voulez-vous vraiment annuler ? Les données non sauvegardées seront perdues.')) {
      navigate('/practitioner-dashboard');
    }
  };

  const generateReport = async () => {
    try {
      setGenerating(true);

      const toothNumbers = formData?.toothNumbers
        ?.split(',')
        ?.map(t => t?.trim())
        ?.filter(Boolean) || [];

      const procedures = formData?.procedures
        ?.split(',')
        ?.map(p => p?.trim())
        ?.filter(Boolean) || [];

      // Upload images to storage if any
      let uploadedImageData = [];
      if (uploadedImages?.length > 0 && draftReportId && userProfile?.id) {
        toast?.loading('Téléchargement des images...', { id: 'upload-images' });
        
        try {
          const uploadPromises = uploadedImages?.map(async (img) => {
            if (img?.file) {
              const result = await reportImageStorageService?.uploadImage(
                img?.file,
                userProfile?.id,
                draftReportId
              );
              return {
                type: 'image',
                url: result?.url,
                path: result?.path,
                fileName: result?.fileName,
                fileSize: result?.fileSize,
                fileType: result?.fileType
              };
            }
            return null;
          });
          
          uploadedImageData = (await Promise.all(uploadPromises))?.filter(Boolean);
          toast?.success('Images téléchargées', { id: 'upload-images' });
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast?.error('Erreur lors du téléchargement des images', { id: 'upload-images' });
        }
      }

      const reportData = {
        practitionerId: userProfile?.practitionerId,
        authorId: userProfile?.id,
        referralId: formData?.patientSource === 'existing' ? formData?.selectedReferralId : null,
        patientFirstName: formData?.patientFirstName,
        patientLastName: formData?.patientLastName,
        patientYearOfBirth: formData?.patientYearOfBirth ? parseInt(formData?.patientYearOfBirth) : null,
        patientIdReference: formData?.patientIdReference,
        referrerName: formData?.referrerName,
        referrerSpecialty: formData?.referrerSpecialty,
        referrerPractice: formData?.referrerPractice,
        referrerEmail: formData?.referrerEmail,
        referrerPhone: formData?.referrerPhone,
        reportType: formData?.reportType,
        sourceNotes: formData?.sourceNotes,
        toothNumbers,
        diagnosis: formData?.diagnosis,
        proceduresPerformed: procedures,
        attachments: uploadedImageData
      };

      // Create or update draft report
      let reportId = draftReportId;
      if (!reportId) {
        const createdReport = await endoReportService?.create(reportData);
        reportId = createdReport?.id;
        setDraftReportId(reportId);
      } else {
        await endoReportService?.update(reportId, reportData);
      }

      // Generate AI content
      const content = await endoReportService?.generateContent(reportId, formData?.sourceNotes);
      setGeneratedContent(content);

      toast?.success('Compte rendu généré avec succès');
    } catch (error) {
      console.error('Error generating report:', error);
      toast?.error(error?.message || 'Erreur lors de la génération du compte rendu');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setGenerating(true);
      await generateReport();
    } catch (error) {
      // Error already handled in generateReport
    }
  };

  const handleContentChange = (newContent) => {
    setGeneratedContent(newContent);
  };

  const handleRefine = async (instruction) => {
    try {
      setRefining(true);
      
      const refinedContent = await endoReportService?.refineContent(
        draftReportId,
        generatedContent,
        instruction
      );

      setGeneratedContent(refinedContent);

      // Update draft with refined content
      await endoReportService?.update(draftReportId, {
        content: refinedContent
      });

      toast?.success('Modification appliquée avec succès');
    } catch (error) {
      console.error('Error refining content:', error);
      toast?.error(error?.message || 'Erreur lors du raffinement du contenu');
    } finally {
      setRefining(false);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);

      // Update report with final content
      await endoReportService?.update(draftReportId, {
        content: generatedContent,
        status: 'final'
      });

      toast?.success('Compte rendu finalisé avec succès');
      navigate(`/report-editor-preview?reportId=${draftReportId}`);
    } catch (error) {
      console.error('Error finalizing report:', error);
      toast?.error('Erreur lors de la finalisation du compte rendu');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PatientSelectionStep
            formData={formData}
            referrals={referrals}
            contacts={contacts}
            onInputChange={handleInputChange}
            onReferralSelection={handleReferralSelection}
            onReferrerSelection={handleReferrerSelection}
          />
        );
      case 2:
        return (
          <ReportTypeStep
            formData={formData}
            onInputChange={handleInputChange}
          />
        );
      case 3:
        return (
          <DictationStep
            formData={formData}
            onInputChange={handleInputChange}
          />
        );
      case 4:
        return (
          <ImageUploadStep
            images={uploadedImages}
            onImagesChange={setUploadedImages}
            reportId={draftReportId}
            practitionerId={userProfile?.id}
          />
        );
      case 5:
        return (
          <ReviewEditStep
            generatedContent={generatedContent}
            onContentChange={handleContentChange}
            onRegenerate={handleRegenerate}
            isRegenerating={generating}
          />
        );
      case 6:
        return (
          <RefinementStep
            currentContent={generatedContent}
            onRefine={handleRefine}
            isRefining={refining}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Générateur de Compte Rendu IA | EndoReferral</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        {/* Step Progress Indicator */}
        <StepProgressIndicator
          currentStep={currentStep}
          totalSteps={totalSteps}
          stepLabels={stepLabels}
          completedSteps={Array.from({ length: currentStep - 1 }, (_, i) => i + 1)}
        />

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-6 py-12">
          {loading && currentStep === 1 ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Workflow Navigation */}
        <WorkflowNavigation
          currentStep={currentStep}
          totalSteps={totalSteps}
          onNext={currentStep === totalSteps ? handleFinish : handleNext}
          onPrevious={handlePrevious}
          onCancel={handleCancel}
          isNextDisabled={generating || refining}
          isLoading={generating || refining || (loading && currentStep === totalSteps)}
          nextLabel={currentStep === 4 ? 'Générer' : currentStep === totalSteps ? 'Finaliser' : 'Continuer'}
          showCancel={true}
        />
      </div>
    </>
  );
};

export default AIReportGenerator;